class CustomHeader extends HTMLElement {
    connectedCallback() {
        if (this.dataset.liveHeader === 'true') {
            document.documentElement.classList.add('header-v2-active');
        }

        this.section = this.closest('.custom-header-section');
        this.stickyMode = this.getAttribute('sticky') || 'never';
        this.lastScrollTop = window.scrollY || 0;
        this.navItems = this.querySelectorAll('.custom-header__nav-item');

        this.updateHeaderHeight();

        this.resizeObserver = new ResizeObserver(() => {
            this.updateHeaderHeight();
        });

        this.resizeObserver.observe(this);

        if (this.stickyMode !== 'never') {
            this.section?.classList.add('custom-header-section--sticky');
            window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        this.megaCloseTimer = null;

        this.navItems.forEach((item) => {
            const mega = item.querySelector('.custom-header-mega');

            if (!mega) return;

            item.addEventListener('mouseenter', () => this.openMega(item));
            item.addEventListener('mouseleave', () => this.scheduleCloseMega(item));
            item.addEventListener('focusout', this.onNavItemFocusout);

            item.querySelector('[data-mega-toggle]')?.addEventListener('click', this.onMegaToggleClick);

            mega.addEventListener('mouseenter', () => this.openMega(item));
            mega.addEventListener('mouseleave', () => this.scheduleCloseMega(item));
        });

        document.addEventListener('keydown', this.onMegaKeydown);

        this.headerGroup = document.querySelector('#header-group');

        if (this.stickyMode !== 'never') {
            this.headerGroup?.classList.add('custom-header-group--sticky');
            window.addEventListener('scroll', this.onScroll, { passive: true });
        }

        this.mobileToggle = this.querySelector('[data-mobile-menu-toggle]');
        this.mobileTabs = this.querySelectorAll('[data-mobile-tab-button]');

        this.mobileToggle?.addEventListener('click', this.toggleMobileMenu);

        this.mobileTabs.forEach((button) => {
            button.addEventListener('click', this.onMobileTabClick);
        });
    }

    openMega = (item) => {
        window.clearTimeout(this.megaCloseTimer);

        for (const navItem of this.navItems) {
            if (navItem !== item) this.setMegaState(navItem, false);
        }

        this.setMegaState(item, true);
        document.body.classList.add('custom-header-mega-open');
    };

    /**
     * @param {Element} item
     * @param {boolean} isOpen
     */
    setMegaState = (item, isOpen) => {
        item.classList.toggle('is-mega-open', isOpen);
        item.querySelector('[data-mega-toggle]')?.setAttribute('aria-expanded', String(isOpen));
    };

    /**
     * @param {Element} item
     */
    closeMega = (item) => {
        window.clearTimeout(this.megaCloseTimer);
        this.setMegaState(item, false);

        if (!this.querySelector('.custom-header__nav-item.is-mega-open')) {
            document.body.classList.remove('custom-header-mega-open');
        }
    };

    scheduleCloseMega = (item) => {
        window.clearTimeout(this.megaCloseTimer);

        this.megaCloseTimer = window.setTimeout(() => {
            const isStillHoveringItem = item.matches(':hover');
            const isStillHoveringMega = item.querySelector('.custom-header-mega:hover');
            const hasKeyboardFocus = item.querySelector(':focus-visible');

            if (isStillHoveringItem || isStillHoveringMega || hasKeyboardFocus) return;

            this.closeMega(item);
        }, 250);
    };

    /**
     * A click while the pointer is over the item never closes it, so a hover-opened
     * menu is not shut by the same click that was meant to open it.
     * @param {MouseEvent} event
     */
    onMegaToggleClick = (event) => {
        if (!(event.currentTarget instanceof Element)) return;

        const item = event.currentTarget.closest('.custom-header__nav-item');

        if (!item) return;

        if (item.classList.contains('is-mega-open') && !item.matches(':hover')) {
            this.closeMega(item);
            return;
        }

        this.openMega(item);
    };

    /**
     * @param {FocusEvent} event
     */
    onNavItemFocusout = (event) => {
        const item = event.currentTarget;

        if (!(item instanceof Element)) return;
        if (event.relatedTarget instanceof Node && item.contains(event.relatedTarget)) return;
        if (item.matches(':hover')) return;

        this.closeMega(item);
    };

    /**
     * @param {KeyboardEvent} event
     */
    onMegaKeydown = (event) => {
        if (event.key !== 'Escape') return;

        const item = this.querySelector('.custom-header__nav-item.is-mega-open');

        if (!item) return;

        const hadFocus = item.contains(document.activeElement);

        this.closeMega(item);

        const toggle = item.querySelector('[data-mega-toggle]');

        if (hadFocus && toggle instanceof HTMLElement) toggle.focus();
    };

    toggleMobileMenu = () => {
        this.setMobileMenuState(!this.classList.contains('is-mobile-menu-open'));
    };

    /**
     * Opens or closes the mobile drawer and keeps its accessibility state in step.
     * The menu button stays outside the drawer and is the drawer's close control,
     * so it is part of the focus loop while the drawer is open.
     * @param {boolean} isOpen
     */
    setMobileMenuState = (isOpen) => {
        const drawer = this.querySelector('[data-mobile-drawer]');

        this.classList.toggle('is-mobile-menu-open', isOpen);
        document.body.classList.toggle('custom-header-mobile-open', isOpen);

        if (this.mobileToggle) {
            const label = isOpen ? this.mobileToggle.dataset.labelClose : this.mobileToggle.dataset.labelOpen;

            this.mobileToggle.setAttribute('aria-expanded', String(isOpen));
            if (label) this.mobileToggle.setAttribute('aria-label', label);
        }

        if (!drawer) return;

        drawer.setAttribute('aria-hidden', String(!isOpen));
        drawer.inert = !isOpen;

        if (isOpen) {
            document.addEventListener('keydown', this.onMobileMenuKeydown);
            this.getDrawerFocusables(drawer)[0]?.focus();
            return;
        }

        document.removeEventListener('keydown', this.onMobileMenuKeydown);

        if (drawer.contains(document.activeElement)) this.mobileToggle?.focus();
    };

    /**
     * @param {Element} drawer
     * @returns {HTMLElement[]}
     */
    getDrawerFocusables = (drawer) => {
        const selector = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

        return [...drawer.querySelectorAll(selector)].filter(
            (element) => element instanceof HTMLElement && element.getClientRects().length > 0
        );
    };

    /**
     * @param {KeyboardEvent} event
     */
    onMobileMenuKeydown = (event) => {
        if (event.key === 'Escape') {
            event.preventDefault();
            this.setMobileMenuState(false);
            this.mobileToggle?.focus();
            return;
        }

        if (event.key !== 'Tab') return;

        const drawer = this.querySelector('[data-mobile-drawer]');

        if (!drawer || !this.mobileToggle) return;

        const loop = [this.mobileToggle, ...this.getDrawerFocusables(drawer)];
        const first = loop[0];
        const last = loop[loop.length - 1];
        const isInsideLoop = loop.includes(document.activeElement);

        if (!isInsideLoop) {
            event.preventDefault();
            first.focus();
            return;
        }

        if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
            return;
        }

        if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
        }
    };

    /**
     * @param {Element} tab
     * @param {boolean} isOpen
     */
    setMobileTabState = (tab, isOpen) => {
        tab.classList.toggle('is-open', isOpen);
        tab.querySelector('[data-mobile-tab-button]')?.setAttribute('aria-expanded', String(isOpen));

        const panel = tab.querySelector('[data-mobile-tab-panel]');

        if (panel) panel.hidden = !isOpen;
    };

    onMobileTabClick = (event) => {
        const currentTab = event.currentTarget.closest('.custom-header-mobile-tab');
        const isOpen = currentTab.classList.contains('is-open');

        for (const tab of this.querySelectorAll('.custom-header-mobile-tab')) {
            this.setMobileTabState(tab, false);
        }

        document.body.classList.remove('custom-header-mega-open');

        if (isOpen) return;

        this.setMobileTabState(currentTab, true);
        document.body.classList.add('custom-header-mega-open');
    };

    disconnectedCallback() {
        this.resizeObserver?.disconnect();
        window.removeEventListener('scroll', this.onScroll);
        document.body.classList.remove('custom-header-mega-open');
        document.body.style.removeProperty('--header-height');
        this.headerGroup?.classList.remove('custom-header-group--sticky');
        this.headerGroup?.classList.remove('custom-header-group--hidden');

        this.mobileToggle?.removeEventListener('click', this.toggleMobileMenu);

        this.mobileTabs?.forEach((button) => {
            button.removeEventListener('click', this.onMobileTabClick);
        });

        document.body.classList.remove('custom-header-mobile-open');
        document.removeEventListener('keydown', this.onMobileMenuKeydown);
        document.removeEventListener('keydown', this.onMegaKeydown);

        window.clearTimeout(this.megaCloseTimer);

        this.navItems?.forEach((item) => {
            item.removeEventListener('focusout', this.onNavItemFocusout);
            item.querySelector('[data-mega-toggle]')?.removeEventListener('click', this.onMegaToggleClick);
            this.setMegaState(item, false);
        });
    }

    updateHeaderHeight = () => {
        document.body.style.setProperty('--header-height', `${this.offsetHeight}px`);
    };

    onScroll = () => {
        if (this.stickyMode !== 'scroll-up') return;

        const currentScrollTop = window.scrollY || 0;
        const isScrollingDown = currentScrollTop > this.lastScrollTop;

        if (currentScrollTop <= this.offsetHeight) {
            this.headerGroup?.classList.remove('custom-header-group--hidden');
        } else if (isScrollingDown) {
            this.headerGroup?.classList.add('custom-header-group--hidden');
        } else {
            this.headerGroup?.classList.remove('custom-header-group--hidden');
        }

        this.lastScrollTop = currentScrollTop;
    };

    lockScroll = () => {
        document.body.classList.add('custom-header-mega-open');
    };

    unlockScroll = () => {
        window.setTimeout(() => {
            if (!this.querySelector('.custom-header__nav-item:hover')) {
                document.body.classList.remove('custom-header-mega-open');
            }
        }, 50);
    };
}

if (!customElements.get('custom-header')) {
    customElements.define('custom-header', CustomHeader);
}